import { useCallback, useEffect, useRef, useState } from 'react';
import { getWaitings } from '../api/waiting.api';
import type { Waiting } from '../types/waiting';

const POLLING_INTERVAL = 5000;

/** KST 기준 오늘 날짜를 YYYY-MM-DD 문자열로 반환 */
export function getTodayKST(): string {
  const now = new Date();
  const kst = new Date(now.getTime() + 9 * 60 * 60 * 1000);
  return kst.toISOString().slice(0, 10);
}

interface UseWaitingListReturn {
  waitings: Waiting[];
  isLoading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  refresh: () => void;
}

export function useWaitingList(
  storeId: string | null,
  date?: string,
): UseWaitingListReturn {
  const [waitings, setWaitings] = useState<Waiting[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isMountedRef = useRef(true);

  const isToday = !date || date === getTodayKST();

  const fetchData = useCallback(
    async (showLoading = false) => {
      if (!storeId) return;

      if (showLoading) {
        setIsLoading(true);
      }

      try {
        const data = await getWaitings(storeId, date);
        if (isMountedRef.current) {
          setWaitings(data);
          setError(null);
          setLastUpdated(new Date());
        }
      } catch (err) {
        if (isMountedRef.current) {
          setError(
            err instanceof Error ? err.message : '대기 목록을 불러오는 데 실패했습니다.',
          );
        }
      } finally {
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }
    },
    [storeId, date],
  );

  const startPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = setInterval(() => {
      if (!document.hidden) {
        fetchData(false);
      }
    }, POLLING_INTERVAL);
  }, [fetchData]);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // 탭 가시성 변경 처리
  useEffect(() => {
    if (!isToday) return; // 과거 데이터는 가시성 변경 시에도 폴링 안 함

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        fetchData(false);
        startPolling();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchData, startPolling, stopPolling, isToday]);

  // 초기 로드 및 폴링 시작
  useEffect(() => {
    isMountedRef.current = true;

    if (storeId) {
      fetchData(true);
      // 오늘 날짜일 때만 폴링
      if (isToday) {
        startPolling();
      }
    }

    return () => {
      isMountedRef.current = false;
      stopPolling();
    };
  }, [storeId, fetchData, startPolling, stopPolling, isToday]);

  const refresh = useCallback(() => {
    fetchData(false);
  }, [fetchData]);

  return { waitings, isLoading, error, lastUpdated, refresh };
}
